import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import Navbar from './components/Navbar';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Signup from './components/Signup';
import MerchantLogin from './components/MerchantLogin';
import MerchantSignup from './components/MerchantSignup';
import AdminLogin from './components/AdminLogin';
import AdminSignup from './components/AdminSignup';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import AdminMerchantManagement from './components/AdminMerchantManagement';
import AdminBankApiConfig from './components/AdminBankApiConfig';
import AdminPayoutUserManagement from './components/AdminPayoutUserManagement';
import AdminPayoutSecurityConfig from './components/AdminPayoutSecurityConfig';
import AdminPayoutConfig from './components/AdminPayoutConfig';
import AuditLogs from './components/AuditLogs';
import PaymentPage from './components/PaymentPage';
import KYCDashboard from './components/KYCDashboard';
import MerchantKycDashboard from './components/MerchantKycDashboard';
import KYCHelp from './components/KYCHelp';
import KYCDemo from './components/KYCDemo';
import KYCValidation from './components/KYCValidation';
import KYCValidationTester from './components/KYCValidationTester';
import KYCVerificationCenter from './components/KYCVerificationCenter';
import TestDocumentUpload from './components/TestDocumentUpload';
import NotificationSystem from './components/NotificationSystem';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import IdleTimerProvider from './components/IdleTimerProvider';

function App() {
  // Auto-scroll to top when error/success alerts appear
  React.useEffect(() => {
    const observer = new MutationObserver(() => {
      const alert = document.querySelector('.alert-danger, .alert-error, .alert-success');
      if (alert) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <AuthProvider>
      <Router>
        <IdleTimerProvider>
        <div className="App">
          <a href="#main-content" className="skip-link">Skip to main content</a>
          <Navbar />
          <NotificationSystem />
          <main id="main-content">
          <Routes>
            {/* Landing and legacy routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<MerchantLogin />} />
            <Route path="/signup" element={<MerchantSignup />} />
            
            {/* Merchant routes */}
            <Route path="/merchant/login" element={<MerchantLogin />} />
            <Route path="/merchant/signup" element={<MerchantSignup />} />
            
            {/* Admin routes — secret URLs, not linked anywhere in UI */}
            <Route path="/secure-admin-portal/login" element={<AdminLogin />} />
            <Route path="/secure-admin-portal/signup" element={<AdminSignup />} />
            
            {/* Payment route */}
            <Route path="/payment/:transactionId" element={<PaymentPage />} />
            
            {/* Protected routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute merchantOnly={true}>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/kyc" 
              element={
                <ProtectedRoute>
                  <KYCDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/merchant/kyc" 
              element={
                <ProtectedRoute merchantOnly={true}>
                  <MerchantKycDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/kyc/help" 
              element={
                <ProtectedRoute>
                  <KYCHelp />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/kyc/demo" 
              element={
                <KYCDemo />
              } 
            />
            <Route 
              path="/kyc/validation" 
              element={
                <ProtectedRoute>
                  <KYCValidation />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/kyc/test" 
              element={
                <ProtectedRoute>
                  <KYCValidationTester />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/kyc/verify" 
              element={
                <ErrorBoundary>
                  <ProtectedRoute>
                    <KYCVerificationCenter />
                  </ProtectedRoute>
                </ErrorBoundary>
              } 
            />
            <Route 
              path="/test-upload" 
              element={<TestDocumentUpload />} 
            />
            <Route 
              path="/admin" 
              element={
                <ErrorBoundary>
                <ProtectedRoute adminOnly={true}>
                  <AdminDashboard />
                </ProtectedRoute>
                </ErrorBoundary>
              } 
            />
            <Route 
              path="/admin/merchants" 
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminMerchantManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/bank-api-config" 
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminBankApiConfig />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/payout-users" 
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminPayoutUserManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/payout-security-config" 
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminPayoutSecurityConfig />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/payout-config" 
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminPayoutConfig />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/audit-logs" 
              element={
                <ErrorBoundary>
                  <ProtectedRoute adminOnly={true}>
                    <AuditLogs />
                  </ProtectedRoute>
                </ErrorBoundary>
              } 
            />
          </Routes>
          </main>
        </div>
        </IdleTimerProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;